import cn from "classnames";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import styles from "./MultiSelect.module.scss";
import { SpriteIcon } from "@/components/UI/SpriteIcon";
import IconAngleDown from "public/img/icons/svg/angle-down.svg";
import IconAngleRight from "public/img/icons/svg/angle-right.svg";
import IconClose from "public/img/icons/svg/times.svg";

export type MultiSelectProps = {
  list: { id: number | string; customerName: string }[];
  stateList: (number | string)[];
  setSelected: (value: (number | string)[]) => void;
  placeholder?: string;
  className?: string;
  onToggle?: (visible: boolean) => void;
  forceClose?: boolean;
};

export const MultiSelect = ({
  list,
  stateList,
  setSelected,
  placeholder,
  className,
  onToggle,
  forceClose,
}: MultiSelectProps) => {
  const [isShow, setIsShow] = useState(false);

  const select = useRef<HTMLDivElement>(null);

  const handleCheck = (e: ChangeEvent<HTMLInputElement>) => {
    const value = +e.target.value;
    if (e.target.checked) {
      setSelected([...stateList, value]);
    } else {
      setSelected(stateList.filter((item) => item !== value));
    }
  };

  const delItem = (id: string | number) => {
    setSelected(stateList.filter((item) => item !== id));
  };

  const isChecked = (id: string | number) => {
    const list = stateList.filter((item) => item === id);
    return !!list.length;
  };

  useEffect(() => {
    forceClose && setIsShow(false);
  }, [forceClose]);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (!select.current) return;
      !select.current.contains(event.target as Node) && setIsShow(false);
    };
    document.addEventListener("click", onDocClick);
    return removeEventListener("click", onDocClick);
  }, []);

  const toggleList = () => {
    setIsShow(!isShow);
    onToggle?.(!isShow);
  };

  const renderCheckedNumber = () => {
    const checkedNumber = stateList.length;
    if (checkedNumber > 1)
      return <div className={cn(styles.badge, styles.badgeNumber)}>+{checkedNumber - 1}...</div>;
  };
  const placeholderText = () => {
    const firstSubject = list.find((item) => item.id === stateList[0]);
    const isListEmpty = stateList.length === 0;
    return (
      <>
        <div className={cn(styles.placeholder, isListEmpty && styles.placeholderEmpty)}>{placeholder}</div>
        {!isListEmpty && (
          <div className={styles.selected}>
            <div className={cn(styles.badge, styles.badgeItem)}>
              {firstSubject?.customerName}
              <SpriteIcon
                icon={IconClose}
                className={styles.badgeClose}
                onClick={() => firstSubject && delItem(firstSubject.id)}
              />
            </div>
            {renderCheckedNumber()}
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={cn(
        styles.wrapper,
        {
          [styles.wrapperOpen]: isShow,
        },
        className
      )}
      ref={select}
    >
      <div className={styles.overflow} onClick={toggleList}></div>
      {placeholderText()}
      {isShow && (
        <div className={styles.list}>
          {list.map(({ id, customerName }) => (
            <div
              key={id}
              className={cn(styles.option, {
                [styles.optionSelected]: isChecked(id),
              })}
            >
              <label className={styles.label}>
                <input
                  name={String(id)}
                  type="checkbox"
                  value={id}
                  onChange={handleCheck}
                  checked={isChecked(id)}
                  className={styles.checkbox}
                />
                {customerName}
              </label>
            </div>
          ))}
        </div>
      )}

      <SpriteIcon icon={IconAngleRight} className={cn(styles.toggleBtnIcon, styles.toggleBtnIconMobile)} />

      <SpriteIcon icon={IconAngleDown} className={cn(styles.toggleBtnIcon, styles.toggleBtnIconDesktop)} />
    </div>
  );
};
